import type { JSX } from "preact";
import { useState } from "preact/hooks";

import { type Session, ROLE_LABELS, api } from "../api";
import { Card } from "../components/Ui";

/**
 * Saját fiók: jelszócsere.
 *
 * Minden szerepkörnek elérhető. A régi jelszót kérjük: egy nyitva hagyott
 * gépen a munkamenet önmagában ne legyen elég a fiók átvételéhez.
 */
export function AccountView({ session }: { session: Session }): JSX.Element {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [message, setMessage] = useState<{ tone: string; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const mismatch = again.length > 0 && next !== again;

  const submit = async (event: Event): Promise<void> => {
    event.preventDefault();
    if (next !== again) return;
    setBusy(true);
    setMessage(null);
    try {
      await api.changeOwnPassword(current, next);
      setMessage({
        tone: "info",
        text: "A jelszó megváltozott. A többi gépen nyitott munkamenete megszűnt.",
      });
      setCurrent("");
      setNext("");
      setAgain("");
    } catch (e: unknown) {
      setMessage({ tone: "bad", text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Fiókom" hint={`${session.email} · ${ROLE_LABELS[session.role] ?? session.role}`}>
      {message && <p className={`notice notice--${message.tone}`}>{message.text}</p>}
      <form className="form" onSubmit={submit}>
        <label>
          Jelenlegi jelszó
          <input
            type="password"
            autoComplete="current-password"
            required
            value={current}
            onInput={(e) => setCurrent((e.target as HTMLInputElement).value)}
          />
        </label>
        <label>
          Új jelszó
          <input
            type="password"
            autoComplete="new-password"
            minLength={10}
            required
            value={next}
            onInput={(e) => setNext((e.target as HTMLInputElement).value)}
          />
          <small className="muted">Legalább 10 karakter.</small>
        </label>
        <label>
          Új jelszó még egyszer
          <input
            type="password"
            autoComplete="new-password"
            required
            value={again}
            onInput={(e) => setAgain((e.target as HTMLInputElement).value)}
          />
          {mismatch && <small className="notice notice--warn">A két jelszó nem egyezik.</small>}
        </label>
        <div className="form__actions">
          <button
            type="submit"
            className="btn btn--primary"
            disabled={busy || mismatch || next.length < 10}
          >
            {busy ? "Mentés…" : "Jelszó módosítása"}
          </button>
        </div>
      </form>
    </Card>
  );
}
